import React, { useState } from 'react';
import { Sparkles, Loader2, Plus, X, Info, ChevronDown, ChevronUp } from 'lucide-react';
import { toast } from 'react-hot-toast';

/**
 * AcademicsSection - Section 2: Academics & Initiatives
 *
 * Fields:
 * - Courses (array, max 5): title + description + why (140 + 200 + 160 chars)
 * - Professors (array, max 3): name + description + approach (100 + 200 + 160 chars)
 * - Labs/Centers/Initiatives (array, max 3): name + description + problem (140 + 200 + 160 chars)
 */
export default function AcademicsSection({ data, onChange, collegeData, essaySubtype }) {
  const [aiLoading, setAiLoading] = useState(false);
  const [showGuidance, setShowGuidance] = useState(false);

  // Get AI suggestions
  const getAISuggestions = async () => {
    // Validate we have college data
    if (!collegeData || !collegeData.id) {
      toast.error('College data not loaded yet. Please wait a moment and try again.');
      console.error('Missing college data:', collegeData);
      return;
    }

    setAiLoading(true);
    try {
      console.log('🎓 Requesting AI suggestions for academics section:', {
        college_id: collegeData.id,
        has_global_data: !!collegeData.global
      });

      const response = await fetch('/api/why-school/enhance-section', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          college_id: collegeData.id,
          section: 'academics',
          existing_data: data,
          global_college_data: collegeData.global
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API error:', response.status, errorData);
        throw new Error(errorData.error || 'Failed to get suggestions');
      }

      const suggestions = await response.json();
      console.log('✅ Received suggestions:', suggestions);

      // Merge AI suggestions into existing data (fill in "why", "approach", "problem" fields)
      const updates = { ...data };
      let hasUpdates = false;

      // Merge course "why" suggestions
      if (suggestions.course_suggestions && suggestions.course_suggestions.length > 0 && data.courses) {
        updates.courses = data.courses.map(course => {
          const suggestion = suggestions.course_suggestions.find(s =>
            s.title && course.title && s.title.toLowerCase().includes(course.title.toLowerCase().substring(0, 10))
          );
          if (suggestion && suggestion.why_suggestion && !course.why) {
            hasUpdates = true;
            return { ...course, why: suggestion.why_suggestion };
          }
          return course;
        });
      }

      // Merge professor "approach" suggestions
      if (suggestions.professor_suggestions && suggestions.professor_suggestions.length > 0 && data.professors) {
        updates.professors = data.professors.map(prof => {
          const suggestion = suggestions.professor_suggestions.find(s =>
            s.name && prof.name && s.name.toLowerCase().includes(prof.name.toLowerCase().substring(0, 5))
          );
          if (suggestion && suggestion.approach_suggestion && !prof.approach) {
            hasUpdates = true;
            return { ...prof, approach: suggestion.approach_suggestion };
          }
          return prof;
        });
      }

      // Merge initiative "problem" suggestions
      if (suggestions.initiative_suggestions && suggestions.initiative_suggestions.length > 0 && data.initiatives) {
        updates.initiatives = data.initiatives.map(init => {
          const suggestion = suggestions.initiative_suggestions.find(s =>
            s.name && init.name && s.name.toLowerCase().includes(init.name.toLowerCase().substring(0, 10))
          );
          if (suggestion && suggestion.problem_suggestion && !init.problem) {
            hasUpdates = true;
            return { ...init, problem: suggestion.problem_suggestion };
          }
          return init;
        });
      }

      if (hasUpdates) {
        onChange(updates);
        toast.success('AI suggestions added to your existing content! Review and edit as needed.');
      } else {
        toast.success('Your content already has "why" explanations. AI won\'t overwrite them!');
      }
    } catch (err) {
      console.error('Error getting AI suggestions:', err);
      toast.error(`Could not load AI suggestions: ${err.message}`);
    } finally {
      setAiLoading(false);
    }
  };

  // Courses
  const addCourse = () => {
    const courses = data.courses || [];
    if (courses.length >= 5) {
      toast.error('Maximum 5 courses allowed');
      return;
    }
    onChange({
      ...data,
      courses: [...courses, { title: '', description: '', why: '', source: 'manual' }]
    });
  };

  const updateCourse = (index, field, value) => {
    const courses = [...(data.courses || [])];
    courses[index] = { ...courses[index], [field]: value };
    onChange({ ...data, courses });
  };

  const removeCourse = (index) => {
    const courses = [...(data.courses || [])];
    courses.splice(index, 1);
    onChange({ ...data, courses });
  };

  // Professors
  const addProfessor = () => {
    const professors = data.professors || [];
    if (professors.length >= 3) {
      toast.error('Maximum 3 professors allowed');
      return;
    }
    onChange({
      ...data,
      professors: [...professors, { name: '', description: '', approach: '', source: 'manual' }]
    });
  };

  const updateProfessor = (index, field, value) => {
    const professors = [...(data.professors || [])];
    professors[index] = { ...professors[index], [field]: value };
    onChange({ ...data, professors });
  };

  const removeProfessor = (index) => {
    const professors = [...(data.professors || [])];
    professors.splice(index, 1);
    onChange({ ...data, professors });
  };

  // Initiatives
  const addInitiative = () => {
    const initiatives = data.initiatives || [];
    if (initiatives.length >= 3) {
      toast.error('Maximum 3 initiatives allowed');
      return;
    }
    onChange({
      ...data,
      initiatives: [...initiatives, { name: '', description: '', problem: '', source: 'manual' }]
    });
  };

  const updateInitiative = (index, field, value) => {
    const initiatives = [...(data.initiatives || [])];
    initiatives[index] = { ...initiatives[index], [field]: value };
    onChange({ ...data, initiatives });
  };

  const removeInitiative = (index) => {
    const initiatives = [...(data.initiatives || [])];
    initiatives.splice(index, 1);
    onChange({ ...data, initiatives });
  };

  return (
    <div className="space-y-8">
      {/* Section Guidance */}
      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
        <button
          onClick={() => setShowGuidance(!showGuidance)}
          className="flex items-center justify-between w-full text-left"
        >
          <div className="flex items-center space-x-2">
            <Info className="w-5 h-5 text-indigo-600" />
            <span className="font-semibold text-indigo-900">Section Guidance</span>
          </div>
          {showGuidance ? (
            <ChevronUp className="w-5 h-5 text-indigo-600" />
          ) : (
            <ChevronDown className="w-5 h-5 text-indigo-600" />
          )}
        </button>

        {showGuidance && (
          <div className="mt-4 space-y-3 text-sm text-indigo-800">
            <p className="font-medium">What This Section Does:</p>
            <p>
              Move beyond name-dropping programs. Show how you'd <em>use</em> the academic resources.
              Each item should reveal both the school's approach and how it connects to your learning.
            </p>
            <div className="bg-white rounded p-3 border border-indigo-200">
              <p className="font-medium mb-2">Strong Example:</p>
              <p className="italic">
                Course: "6.9000: Software Studio"
                <br />
                Why: "Learn that every interface design is an argument—matches how I think about
                my debate scripts as persuasive architecture."
              </p>
            </div>
          </div>
        )}
      </div>

      {/* AI Suggestions Button */}
      <div className="flex justify-end">
        <button
          onClick={getAISuggestions}
          disabled={aiLoading}
          className="flex items-center space-x-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition disabled:opacity-50"
        >
          {aiLoading ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Loading Suggestions...</span>
            </>
          ) : (
            <>
              <Sparkles className="w-4 h-4" />
              <span>Get AI Suggestions</span>
            </>
          )}
        </button>
      </div>

      {/* Courses */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-gray-900">
            Courses that interest you (up to 5)
          </label>
          <button
            onClick={addCourse}
            disabled={(data.courses || []).length >= 5}
            className="flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            <span>Add Course</span>
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Include full course titles if possible. Then add 5-10 words on <em>why</em> each draws you in.
        </p>

        <div className="space-y-4">
          {(data.courses || []).map((course, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-start space-x-3">
                <div className="flex-1 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Course Title
                    </label>
                    <input
                      type="text"
                      value={course.title || ''}
                      onChange={(e) => updateCourse(index, 'title', e.target.value)}
                      placeholder='e.g., "Urban Design and the Public Realm"'
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      What is this course about?
                    </label>
                    <textarea
                      value={course.description || ''}
                      onChange={(e) => updateCourse(index, 'description', e.target.value)}
                      placeholder="Brief description of course content and focus"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 resize-y min-h-[60px]"
                      style={{ height: 'auto', minHeight: '60px' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Why it draws you in
                    </label>
                    <textarea
                      value={course.why || ''}
                      onChange={(e) => updateCourse(index, 'why', e.target.value)}
                      placeholder="Explores how architecture shapes belonging"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 resize-y min-h-[60px]"
                      style={{ height: 'auto', minHeight: '60px' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  </div>
                </div>

                <button
                  onClick={() => removeCourse(index)}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {(!data.courses || data.courses.length === 0) && (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-600 mb-3">No courses added yet</p>
              <button
                onClick={addCourse}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4" />
                <span>Add Your First Course</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Professors */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-gray-900">
            Professors or Researchers (up to 3)
          </label>
          <button
            onClick={addProfessor}
            disabled={(data.professors || []).length >= 3}
            className="flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            <span>Add Professor</span>
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Focus on <em>approach</em>, not fame. Example: "Professor Rahimi connects data ethics
          to design—exactly how I think about coding."
        </p>

        <div className="space-y-4">
          {(data.professors || []).map((professor, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-start space-x-3">
                <div className="flex-1 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Professor Name
                    </label>
                    <input
                      type="text"
                      value={professor.name || ''}
                      onChange={(e) => updateProfessor(index, 'name', e.target.value)}
                      placeholder="e.g., Professor Rahimi"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      What is their research/work about?
                    </label>
                    <textarea
                      value={professor.description || ''}
                      onChange={(e) => updateProfessor(index, 'description', e.target.value)}
                      placeholder="Brief description of their research focus or teaching approach"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 resize-y min-h-[60px]"
                      style={{ height: 'auto', minHeight: '60px' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Approach / Why relevant
                    </label>
                    <textarea
                      value={professor.approach || ''}
                      onChange={(e) => updateProfessor(index, 'approach', e.target.value)}
                      placeholder="Connects data ethics to design—exactly how I think about coding"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 resize-y min-h-[60px]"
                      style={{ height: 'auto', minHeight: '60px' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  </div>
                </div>

                <button
                  onClick={() => removeProfessor(index)}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {(!data.professors || data.professors.length === 0) && (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-600 mb-3">No professors added yet</p>
              <button
                onClick={addProfessor}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4" />
                <span>Add a Professor</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Labs/Centers/Initiatives */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <label className="block text-sm font-semibold text-gray-900">
            Labs / Centers / Initiatives (up to 3)
          </label>
          <button
            onClick={addInitiative}
            disabled={(data.initiatives || []).length >= 3}
            className="flex items-center space-x-1 text-sm text-indigo-600 hover:text-indigo-700 disabled:opacity-50"
          >
            <Plus className="w-4 h-4" />
            <span>Add Initiative</span>
          </button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Each exists to solve a real problem. Complete: "They're figuring out how to ___."
        </p>

        <div className="space-y-4">
          {(data.initiatives || []).map((initiative, index) => (
            <div key={index} className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="flex items-start space-x-3">
                <div className="flex-1 space-y-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Lab / Center / Initiative Name
                    </label>
                    <input
                      type="text"
                      value={initiative.name || ''}
                      onChange={(e) => updateInitiative(index, 'name', e.target.value)}
                      placeholder="e.g., MIT D-Lab"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      What is this initiative about?
                    </label>
                    <textarea
                      value={initiative.description || ''}
                      onChange={(e) => updateInitiative(index, 'description', e.target.value)}
                      placeholder="Brief description of what this lab/center/initiative does"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 resize-y min-h-[60px]"
                      style={{ height: 'auto', minHeight: '60px' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Problem they're solving
                    </label>
                    <textarea
                      value={initiative.problem || ''}
                      onChange={(e) => updateInitiative(index, 'problem', e.target.value)}
                      placeholder="They're figuring out how to make AI fairer in public policy"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-indigo-500 resize-y min-h-[60px]"
                      style={{ height: 'auto', minHeight: '60px' }}
                      onInput={(e) => {
                        e.target.style.height = 'auto';
                        e.target.style.height = e.target.scrollHeight + 'px';
                      }}
                    />
                  </div>
                </div>

                <button
                  onClick={() => removeInitiative(index)}
                  className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 transition"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}

          {(!data.initiatives || data.initiatives.length === 0) && (
            <div className="text-center py-8 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
              <p className="text-sm text-gray-600 mb-3">No initiatives added yet</p>
              <button
                onClick={addInitiative}
                className="inline-flex items-center space-x-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <Plus className="w-4 h-4" />
                <span>Add an Initiative</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Pro Tip */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">💡 Pro Tip</h4>
        <p className="text-sm text-blue-800">
          You don't need to fill all slots—quality over quantity. One course with a strong
          "why" is better than three generic listings. Show HOW you'd use each resource, not
          just that you're interested.
        </p>
      </div>
    </div>
  );
}
